// EDV Next 0.11.1 - Lube Manager workspace.
// Deliberately organized around the existing typed lubeApi surface. Uses
// design.css design-system classes. NO new API, sync engine, or persistence
// layer.
import React, { useEffect, useMemo, useState } from 'react';
import { lubeApi, type LubeImportPreview, type LubePoint, type LubeRoute } from './lube-api.js';
import { useSession } from './session.js';
import { useSync } from './sync.js';
import { EmptyState, ErrorState, Skeleton, useConfirm, useToasts } from './shell.js';

type Tab = 'points' | 'routes' | 'lubricants' | 'tools' | 'import' | 'calc';
type PointMode = 'bearing' | 'gearbox' | 'pinion' | 'freehand' | 'autogreaser' | 'other';

export function LubeManager({ siteId }: { siteId: string }) {
  const sess = useSession();
  const [tab, setTab] = useState<Tab>('points');
  const tabs: Array<{ id: Tab; label: string }> = [
    { id: 'points',     label: 'Points' },
    { id: 'routes',     label: 'Routes' },
    { id: 'lubricants', label: 'Lubricants' },
    { id: 'tools',      label: 'Tools' },
    { id: 'import',     label: 'Import' },
    { id: 'calc',       label: 'Calc preview' },
  ];
  return (
    <section aria-label="Lube Manager" data-testid="lube-workspace">
      <h2>Lube Manager</h2>
      <div className="edv-tabs" role="tablist" aria-label="Lube Manager tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            aria-current={tab === t.id ? 'page' : undefined}
            tabIndex={tab === t.id ? 0 : -1}
            onClick={() => setTab(t.id)}
            data-testid={`lube-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {sess.me ? (
        <>
          {tab === 'points'     && <PointsPane     siteId={siteId} deviceId={sess.me.device.id} />}
          {tab === 'routes'     && <RoutesPane     siteId={siteId} deviceId={sess.me.device.id} />}
          {tab === 'lubricants' && <LubricantsPane siteId={siteId} />}
          {tab === 'tools'      && <ToolsPane />}
          {tab === 'import'     && <ImportPane    siteId={siteId} deviceId={sess.me.device.id} />}
          {tab === 'calc'       && <CalcPreviewPane />}
        </>
      ) : (
        <Skeleton lines={4} wide />
      )}
    </section>
  );
}

function useMaintenanceGuard() {
  const sync = useSync();
  const readonly = sync.state === 'auth_required' || sync.state === 'failed';
  return { readonly, state: sync.state };
}

/* ---------- Points ---------- */

function PointsPane({ siteId, deviceId }: { siteId: string; deviceId: string }) {
  const toast = useToasts();
  const confirm = useConfirm();
  const guard = useMaintenanceGuard();
  const [list, setList] = useState<{ data: LubePoint[]; cursor: string | null } | null>(null);
  const [err, setErr] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<LubePoint | null>(null);

  async function load(cursor?: string) {
    setLoading(true); setErr(null);
    try {
      const r = await lubeApi.listPoints(siteId, cursor ? { cursor, limit: 25 } : { limit: 25 });
      setList(r);
    } catch (e) { setErr(e as Error); }
    finally { setLoading(false); }
  }
  useEffect(() => { setSelected(null); setList(null); void load(); }, [siteId]);

  return (
    <div>
      <div className="edv-toolbar">
        {guard.readonly && <span className="badge warn" role="status">Read-only ({guard.state})</span>}
        <div className="spacer" />
        <button className="btn primary" onClick={() => setCreating(true)} disabled={guard.readonly} data-testid="lube-new-point">
          + New point
        </button>
      </div>
      {err && <ErrorState error={err} onRetry={() => void load()} />}
      {loading && !list && <Skeleton lines={4} wide />}
      {!loading && list && list.data.length === 0 && (
        <EmptyState
          title="No lubrication points"
          hint="Create the first point for this site or import from CSV."
          action={<button className="btn primary" onClick={() => setCreating(true)}>+ New point</button>}
          testid="lube-points-empty"
        />
      )}
      {list && list.data.length > 0 && (
        <table className="edv-table" aria-label="Lubrication points">
          <thead>
            <tr>
              <th>Name</th>
              <th>Mode</th>
              <th>Instruction</th>
              <th>Frequency</th>
              <th>Version</th>
              <th style={{ width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {list.data.map((p) => (
              <tr key={p.id} onClick={() => setSelected(p)} style={{ cursor: 'pointer' }} data-testid={`lube-point-${p.id}`}>
                <td>{p.name}</td>
                <td><span className="badge">{p.point_type}</span></td>
                <td>{formatQtyUnit(p.data)}</td>
                <td>{formatFreqDays(p.data)}</td>
                <td className="mono">v{p.version}</td>
                <td className="tiny muted">Open ›</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {list?.cursor && (
        <div className="edv-toolbar" style={{ justifyContent: 'center', marginTop: 12 }}>
          <button className="btn" onClick={() => void load(list.cursor!)} data-testid="lube-next-page">Next page</button>
        </div>
      )}
      {creating && (
        <CreatePointDialog
          siteId={siteId}
          deviceId={deviceId}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); void load(); toast.push('Point created', 'ok'); }}
        />
      )}
      {selected && (
        <PointDetailDialog
          point={selected}
          deviceId={deviceId}
          onClose={() => setSelected(null)}
          onChanged={() => { void load(); toast.push('Point updated', 'ok'); }}
          confirm={confirm}
          toast={toast}
        />
      )}
    </div>
  );
}

function CreatePointDialog({
  siteId, deviceId, onClose, onCreated,
}: { siteId: string; deviceId: string; onClose(): void; onCreated(): void }) {
  const [name, setName] = useState('');
  const [mode, setMode] = useState<PointMode>('bearing');
  const [qty, setQty]  = useState('');
  const [unit, setUnit] = useState('g');
  const [freq, setFreq] = useState('');
  const [assetId, setAssetId] = useState('');
  const [err, setErr] = useState<Error | null>(null);
  const [submitting, setSubmitting] = useState(false);
  return (
    <div className="edv-dialog-back" role="dialog" aria-modal="true" aria-labelledby="new-point-title" onClick={onClose}>
      <div className="edv-dialog" onClick={(e) => e.stopPropagation()}>
        <header>
          <span id="new-point-title" className="title">New lubrication point</span>
          <button aria-label="Close" style={{ background: 'none', border: 'none', color: '#fff', fontSize: 16, cursor: 'pointer' }} onClick={onClose}>×</button>
        </header>
        <form onSubmit={async (e) => {
          e.preventDefault(); setSubmitting(true);
          try {
            await lubeApi.createPoint(siteId, deviceId, {
              name, point_type: mode, asset_id: assetId,
              data: {
                instruction_qty: qty ? Number(qty) : null,
                instruction_unit: unit,
                freq_days: freq ? Number(freq) : null,
              },
            });
            onCreated();
          } catch (er) { setErr(er as Error); }
          finally { setSubmitting(false); }
        }}>
          <div className="body">
            <div className="form-field"><label>Point name</label><input value={name} onChange={(e) => setName(e.target.value)} required autoFocus data-testid="new-point-name" /></div>
            <div className="form-field"><label>Asset ID</label><input value={assetId} onChange={(e) => setAssetId(e.target.value)} required placeholder="uuid of the asset this point belongs to" className="mono" /></div>
            <div className="form-field">
              <label>Point mode</label>
              <select value={mode} onChange={(e) => setMode(e.target.value as PointMode)}>
                <option value="bearing">Bearing</option>
                <option value="gearbox">Gearbox</option>
                <option value="pinion">Pinion</option>
                <option value="freehand">Freehand</option>
                <option value="autogreaser">Autogreaser</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="row">
              <div className="form-field grow"><label>Qty</label><input type="number" step="0.01" value={qty} onChange={(e) => setQty(e.target.value)} /></div>
              <div className="form-field grow"><label>Unit</label><select value={unit} onChange={(e) => setUnit(e.target.value)}><option value="g">grams</option><option value="ml">mL</option><option value="L">L</option><option value="stroke">strokes</option></select></div>
              <div className="form-field grow"><label>Frequency (days)</label><input type="number" value={freq} onChange={(e) => setFreq(e.target.value)} /></div>
            </div>
            {err && <p className="form-error" role="alert">{err.message}</p>}
          </div>
          <div className="actions">
            <button type="button" className="btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn primary" disabled={submitting}>{submitting ? 'Saving…' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PointDetailDialog({
  point, deviceId, onClose, onChanged, confirm, toast,
}: { point: LubePoint; deviceId: string; onClose(): void; onChanged(): void; confirm: ReturnType<typeof useConfirm>; toast: ReturnType<typeof useToasts> }) {
  const [name, setName] = useState(point.name);
  const [err, setErr] = useState<Error | null>(null);
  useEffect(() => setName(point.name), [point]);
  async function save() {
    try {
      await lubeApi.patchPoint(point.id, deviceId, point.version, { name });
      onChanged();
    } catch (er) { setErr(er as Error); }
  }
  async function toggleArchive() {
    const archived = Boolean(point.archived_at);
    const yes = await confirm({
      title: archived ? 'Restore this point?' : 'Archive this point?',
      body: archived ? 'The point will re-appear in active lists.' : 'The point will be hidden from active lists but remains recoverable.',
      destructive: !archived,
      confirmText: archived ? 'Restore' : 'Archive',
    });
    if (!yes) return;
    try {
      const fn = archived ? lubeApi.restorePoint : lubeApi.archivePoint;
      await fn.call(lubeApi, point.id, deviceId, point.version);
      onChanged();
      toast.push(archived ? 'Point restored' : 'Point archived', 'ok');
    } catch (er) { toast.push((er as Error).message, 'danger'); }
  }
  return (
    <div className="edv-dialog-back" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="edv-dialog" onClick={(e) => e.stopPropagation()}>
        <header><span className="title">{point.name}</span><button aria-label="Close" style={{ background: 'none', border: 'none', color: '#fff', fontSize: 16, cursor: 'pointer' }} onClick={onClose}>×</button></header>
        <div className="body">
          <div className="form-field"><label>Name</label><input value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div className="row"><span className="chip">{point.point_type}</span><span className="chip">v{point.version}</span></div>
          {err && <p className="form-error" role="alert">{err.message}</p>}
        </div>
        <div className="actions">
          <button className="btn danger" onClick={toggleArchive}>{point.archived_at ? 'Restore' : 'Archive'}</button>
          <div className="spacer" />
          <button className="btn" onClick={onClose}>Close</button>
          <button className="btn primary" onClick={save}>Save</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Routes ---------- */

function RoutesPane({ siteId, deviceId }: { siteId: string; deviceId: string }) {
  const toast = useToasts();
  const [routes, setRoutes] = useState<LubeRoute[] | null>(null);
  const [err, setErr] = useState<Error | null>(null);
  const [creating, setCreating] = useState(false);
  async function load() {
    setErr(null);
    try {
      const r = await lubeApi.listRoutes(siteId);
      setRoutes(r.data);
    } catch (e) { setErr(e as Error); }
  }
  useEffect(() => { setRoutes(null); void load(); }, [siteId]);
  return (
    <div>
      <div className="edv-toolbar">
        <div className="spacer" />
        <button className="btn primary" onClick={() => setCreating(true)} data-testid="lube-new-route">+ New route</button>
      </div>
      {err && <ErrorState error={err} onRetry={() => void load()} />}
      {!routes && <Skeleton lines={3} wide />}
      {routes && routes.length === 0 && (
        <EmptyState title="No routes" hint="Group your lubrication points into ordered inspection routes." testid="lube-routes-empty" />
      )}
      {routes && routes.length > 0 && (
        <table className="edv-table" aria-label="Lube routes">
          <thead><tr><th>Name</th><th>Stops</th><th>Version</th></tr></thead>
          <tbody>
            {routes.map((r) => (
              <tr key={r.id}><td>{r.name}</td><td>{r.stops?.length ?? 0}</td><td className="mono">v{r.version}</td></tr>
            ))}
          </tbody>
        </table>
      )}
      {creating && (
        <CreateRouteDialog siteId={siteId} deviceId={deviceId} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); void load(); toast.push('Route created', 'ok'); }} />
      )}
    </div>
  );
}

function CreateRouteDialog({ siteId, deviceId, onClose, onCreated }: { siteId: string; deviceId: string; onClose(): void; onCreated(): void }) {
  const [name, setName] = useState('');
  const [err, setErr] = useState<Error | null>(null);
  return (
    <div className="edv-dialog-back" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="edv-dialog" onClick={(e) => e.stopPropagation()}>
        <header><span className="title">New route</span><button aria-label="Close" style={{ background: 'none', border: 'none', color: '#fff', fontSize: 16, cursor: 'pointer' }} onClick={onClose}>×</button></header>
        <form onSubmit={async (e) => {
          e.preventDefault();
          try {
            await lubeApi.createRoute(siteId, deviceId, name);
            onCreated();
          } catch (er) { setErr(er as Error); }
        }}>
          <div className="body">
            <div className="form-field"><label>Route name</label><input value={name} onChange={(e) => setName(e.target.value)} required autoFocus /></div>
            {err && <p className="form-error" role="alert">{err.message}</p>}
          </div>
          <div className="actions"><button type="button" className="btn" onClick={onClose}>Cancel</button><button type="submit" className="btn primary">Create</button></div>
        </form>
      </div>
    </div>
  );
}

/* ---------- Lubricants / Tools (list-only from existing surfaces) ---------- */

function LubricantsPane({ siteId }: { siteId: string }) {
  // Lubricants surface intentionally read-only in this release; owner manages
  // the register through the admin RPC. The panel documents it clearly.
  void siteId;
  return (
    <EmptyState
      title="Lubricant register"
      hint="The lubricant register is administered through the reliability engineer workflow. The public list endpoint is scheduled for the next minor release; use the preprod seed to populate representative rows for testing."
      testid="lube-lubricants-empty"
    />
  );
}

function ToolsPane() {
  return (
    <EmptyState
      title="Tools register"
      hint="Tools inventory (grease guns, oiler codes, torque wrenches) is scheduled for the next minor release. Point-mode selection already supports 'freehand' and 'autogreaser'."
      testid="lube-tools-empty"
    />
  );
}

/* ---------- Import ---------- */

function ImportPane({ siteId, deviceId }: { siteId: string; deviceId: string }) {
  const [preview, setPreview] = useState<LubeImportPreview | null>(null);
  const [csv, setCsv] = useState('');
  const [err, setErr] = useState<Error | null>(null);
  async function doPreview() {
    setErr(null);
    try {
      const rows = csv.split('\n').filter(Boolean).map((line) => {
        const [name, asset_id, point_type, qty, unit, freq_days] = line.split(',');
        return { name, asset_id, point_type, data: { instruction_qty: Number(qty), instruction_unit: unit, freq_days: Number(freq_days) } };
      });
      const r = await lubeApi.previewImport(siteId, rows);
      setPreview(r);
    } catch (e) { setErr(e as Error); }
  }
  async function apply() {
    if (!preview) return;
    try {
      await lubeApi.applyImport(siteId, deviceId, preview, 'skip');
    } catch (e) { setErr(e as Error); }
  }
  return (
    <div className="col">
      <div className="form-field">
        <label>CSV rows (name,asset_id,point_type,qty,unit,freq_days)</label>
        <textarea rows={6} value={csv} onChange={(e) => setCsv(e.target.value)} data-testid="lube-import-csv" />
        <p className="help">Preview stages rows for validation; Apply commits to the DB.</p>
      </div>
      {err && <ErrorState error={err} onRetry={() => doPreview()} />}
      <div className="edv-toolbar">
        <button className="btn" onClick={() => void doPreview()} data-testid="lube-preview">Preview</button>
        <button className="btn primary" disabled={!preview} onClick={() => void apply()} data-testid="lube-apply">Apply</button>
      </div>
      {preview && (
        <div className="edv-card">
          <div className="card-title">Preview</div>
          <p className="small">Accepted: {preview.accepted.length} · Rejected: {preview.rejected.length} · Conflicts: {preview.conflicts.length}</p>
        </div>
      )}
    </div>
  );
}

/* ---------- Calc preview ---------- */

function CalcPreviewPane() {
  const [rpm, setRpm] = useState('1500');
  const [bore, setBore] = useState('40');
  const [width, setWidth] = useState('15');
  const [result, setResult] = useState<unknown>(null);
  const [err, setErr] = useState<Error | null>(null);
  async function run() {
    setErr(null);
    try {
      const r = await lubeApi.calcPreview('bearing_grease_qty', { rpm: Number(rpm), bore_mm: Number(bore), width_mm: Number(width) });
      setResult(r);
    } catch (e) { setErr(e as Error); }
  }
  return (
    <div className="col">
      <p className="small muted">Preview the shared calculation package result for a bearing configuration.</p>
      <div className="row">
        <div className="form-field grow"><label>RPM</label><input type="number" value={rpm} onChange={(e) => setRpm(e.target.value)} /></div>
        <div className="form-field grow"><label>Bore (mm)</label><input type="number" value={bore} onChange={(e) => setBore(e.target.value)} /></div>
        <div className="form-field grow"><label>Width (mm)</label><input type="number" value={width} onChange={(e) => setWidth(e.target.value)} /></div>
      </div>
      <div className="edv-toolbar"><button className="btn primary" onClick={() => void run()}>Preview</button></div>
      {err && <ErrorState error={err} />}
      {result != null && (
        <pre className="edv-card" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{JSON.stringify(result, null, 2)}</pre>
      )}
    </div>
  );
}

// ---- data formatters (narrow unknown safely for display) --------------------
function narrowText(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return '';
}
function narrowNumber(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.length > 0) {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
function formatQtyUnit(data: Record<string, unknown> | undefined): string {
  if (!data) return '';
  const qtyStr = narrowText(data.instruction_qty);
  const unitStr = narrowText(data.instruction_unit);
  if (qtyStr && unitStr) return qtyStr + ' ' + unitStr;
  return qtyStr;
}
function formatFreqDays(data: Record<string, unknown> | undefined): string {
  if (!data) return '';
  const n = narrowNumber(data.freq_days);
  return n === null ? '' : `${n} d`;
}

